import React from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import { snakeCase } from 'lodash';

import {
  HeroBanner as HeroBannerCL, breakpointValues
} from '@comicrelief/component-library';
import { getImage } from 'gatsby-plugin-image';

import { paddingOptionMapping, textOptionsMapping } from '../../utils/optionMapping';
import renderRichText from '../RichText/RichText';

import imagePropType, { videoPropType } from '../../commonPropTypes/commonPropTypes';

const HeroBanner = ({ data }) => {
  const {
    body,
    type,
    componentBackgroundColour,
    openCtaInNewTab,
    paddingAbove,
    paddingBelow,
    pageBackgroundColour,
    copyOrientation,
    ctaColourTextBannerOnly,
    ctaText,
    ctaUrl,
    loopAmbientVideo,
    lVideo,
    smVideo,
    lImage,
    mImage,
    sImage,
    logo
  } = data;

  // Transforms from CMS 'label' values:
  const thisVariant = type ? snakeCase(type) : undefined;
  const pageBgColour = pageBackgroundColour ? snakeCase(pageBackgroundColour) : undefined;

  // Only actually used as a bg colour on Text Banner (other variants always have white
  // behind the copy), but this is still required for the renderRichText logic:
  const componentBgColour = thisVariant === 'text_banner' && componentBackgroundColour
    ? snakeCase(componentBackgroundColour) : 'white';

  // Only set when present; otherwise let component prop default (white) take over:
  const textBannerCtaColour = ctaColourTextBannerOnly
    ? snakeCase(ctaColourTextBannerOnly) : undefined;

  // Map the human-readable labels to the actual values:
  const paddingTop = paddingAbove ? paddingOptionMapping[paddingAbove] : undefined;
  const paddingBottom = paddingBelow ? paddingOptionMapping[paddingBelow] : undefined;
  const copyPos = copyOrientation ? textOptionsMapping[copyOrientation] : undefined;

  // Grab images:
  const lImageData = getImage(lImage);
  const mImageData = getImage(mImage);
  const sImageData = getImage(sImage);
  const logoImageData = getImage(logo);

  // Set up desktop/L image variables
  const largeImageLow = lImageData?.placeholder?.fallback;
  const largeImageSet = lImageData?.images?.sources[0]?.srcSet;

  // Set up tablet/M image variables
  const mediumImageLow = mImageData?.placeholder?.fallback;
  const mediumImageSet = mImageData?.images?.sources[0]?.srcSet;

  // Set up mobile/S image variables
  const smallImageLow = sImageData?.placeholder?.fallback;
  const smallImageSet = sImageData?.images?.sources[0]?.srcSet;

  // Use the Logo fallback src directly as we don't need to do anything fancy here:
  const logoImage = logoImageData?.images.fallback.src;

  const mediumPosterData = mImage?.posterImageData;
  const largePosterData = lImage?.posterImageData;

  const mediumPosterUrl = mediumPosterData?.images?.fallback?.src
    ?? mImageData?.images?.fallback?.src ?? mImage?.file?.url ?? null;

  const largePosterUrl = largePosterData?.images?.fallback?.src
    ?? lImageData?.images?.fallback?.src ?? lImage?.file?.url ?? null;

  const videoDesktop = lVideo?.file?.url;
  const videoMobile = smVideo?.file?.url;
  const videoLoopProp = loopAmbientVideo ?? true;

  return (
    <>
      {/* Small / mobile: */}
      <MediaQuery maxWidth={breakpointValues.M - 1}>
        <HeroBannerCL
          pageBackgroundColour={pageBgColour}
          textBannerCopyBackgroundColour={componentBgColour}
          variant={thisVariant}
          textBannerCtaColour={textBannerCtaColour}
          ctaText={ctaText}
          ctaUrl={ctaUrl || null}
          paddingTop={paddingTop}
          paddingBottom={paddingBottom}
          ctaNewTab={openCtaInNewTab}
          copyLeft={copyPos}
          imageSet={smallImageSet}
          imageLow={smallImageLow}
          logoImage={logoImage}
          videoDesktop={videoDesktop}
          videoMobile={videoMobile}
          posterDesktop={mediumPosterUrl}
          posterMobile={mediumPosterUrl}
          videoLoop={videoLoopProp}
        >
          {renderRichText(body, componentBgColour)}
        </HeroBannerCL>
      </MediaQuery>

      {/* Medium / tablet: */}
      <MediaQuery minWidth={breakpointValues.M} maxWidth={breakpointValues.L - 1}>
        <HeroBannerCL
          pageBackgroundColour={pageBgColour}
          textBannerCopyBackgroundColour={componentBgColour}
          variant={thisVariant}
          textBannerCtaColour={textBannerCtaColour}
          ctaText={ctaText}
          ctaUrl={ctaUrl || null}
          paddingTop={paddingTop}
          paddingBottom={paddingBottom}
          ctaNewTab={openCtaInNewTab}
          copyLeft={copyPos}
          imageSet={mediumImageSet}
          imageLow={mediumImageLow}
          logoImage={logoImage}
          videoDesktop={videoDesktop}
          videoMobile={videoMobile}
          posterDesktop={mediumPosterUrl}
          posterMobile={mediumPosterUrl}
          videoLoop={videoLoopProp}
        >
          {renderRichText(body, componentBgColour)}
        </HeroBannerCL>
      </MediaQuery>

      {/* Large / desktop: */}
      <MediaQuery minWidth={breakpointValues.L}>
        <HeroBannerCL
          pageBackgroundColour={pageBgColour}
          textBannerCopyBackgroundColour={componentBgColour}
          variant={thisVariant}
          textBannerCtaColour={textBannerCtaColour}
          ctaText={ctaText}
          ctaUrl={ctaUrl || null}
          paddingTop={paddingTop}
          paddingBottom={paddingBottom}
          ctaNewTab={openCtaInNewTab}
          copyLeft={copyPos}
          imageSet={largeImageSet}
          imageLow={largeImageLow}
          logoImage={logoImage}
          videoDesktop={videoDesktop}
          videoMobile={videoMobile}
          posterDesktop={largePosterUrl}
          posterMobile={largePosterUrl}
          videoLoop={videoLoopProp}
        >
          {renderRichText(body, componentBgColour)}
        </HeroBannerCL>
      </MediaQuery>
    </>
  );
};

HeroBanner.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.string.isRequired,
    paddingAbove: PropTypes.string,
    paddingBelow: PropTypes.string,
    componentBackgroundColour: PropTypes.string,
    pageBackgroundColour: PropTypes.string,
    copyOrientation: PropTypes.string,
    openCtaInNewTab: PropTypes.bool,
    ctaText: PropTypes.string,
    ctaUrl: PropTypes.string,
    ctaColourTextBannerOnly: PropTypes.string,
    body: PropTypes.shape({
      raw: PropTypes.string
    }),
    loopAmbientVideo: PropTypes.bool,
    lVideo: videoPropType,
    smVideo: videoPropType,
    lImage: imagePropType,
    mImage: imagePropType,
    sImage: imagePropType,
    logo: imagePropType
  }).isRequired
};

export default HeroBanner;
